import React, { useEffect, useState } from "react";
import {
  getAllHybridAutopoolWithdrawals,
  getPendingLevelClaims,
  approveLevelClaims,
  approveLevelClaimsManually,
  rejectLevelClaims,
} from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import Loader from "../../components/loader/Loader";
import Loader2 from "../../components/loader/Loader2";
import CustomTable from "../../components/custom-table/CustomTable";
import CustomTableCheckbox from "../../components/custom-table/CustomTableCheckbox";
import { toast } from "react-toastify";
import "./style.css";

function AllHybridWithdrawals() {
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [activeTab, setActiveTab] = useState("request");
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [todayWithdrawals, setTodayWithdrawals] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const pendingColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "Level", accessor: "level" },
    { Header: "Reward Amount", accessor: "rewardAmount" },
    { Header: "Crypto", accessor: "cryptoAmount" },
    { Header: "Retopup", accessor: "retopupAmount" },
    { Header: "Wallet Address", accessor: "walletAddress" },
    { Header: "Account Number", accessor: "accountNumber" },
    { Header: "IFSC Code", accessor: "ifscCode" },
    { Header: "Date", accessor: "date" },
  ];

  const historyColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "Level", accessor: "level" },
    { Header: "Reward Amount", accessor: "rewardAmount" },
    { Header: "Claimed At", accessor: "claimedAt" },
    { Header: "Txn ID", accessor: "txnId" },
  ];

  const formatLevel = (level, division) =>
    division ? `Level ${level} Part ${division}` : `Level ${level}`;

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [historyRes, pendingRes] = await Promise.all([
        getAllHybridAutopoolWithdrawals(),
        getPendingLevelClaims(),
      ]);

      const historyData = historyRes?.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let todayCount = 0;
      let totalAmountValue = 0;

      historyData.forEach((record) => {
        totalAmountValue += record.rewardAmount || 0;
        const claimedDate = new Date(record.claimedAt);
        claimedDate.setHours(0, 0, 0, 0);
        if (claimedDate.getTime() === today.getTime()) {
          todayCount++;
        }
      });

      const formattedHistory = historyData.map((record, index) => ({
        sno: index + 1,
        userId: record.userId || "N/A",
        userName: record.userName || "Unknown",
        userEmail: record.userEmail || "N/A",
        level: record.level || "-",
        rewardAmount: `$${(record.rewardAmount || 0).toFixed(2)}`,
        claimedAt: record.claimedAt ? formatDate(record.claimedAt) : "N/A",
        txnId: record.metadata?.txnId || record.txnId ? (
          <a
            href={`https://bscscan.com/tx/${record.metadata?.txnId || record.txnId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            BSC Scan
          </a>
        ) : (
          "-"
        ),
      }));

      const pendingData = pendingRes?.requests || [];
      const formattedPending = pendingData.map((r, i) => ({
        sno: i + 1,
        _id: r._id,
        userId: r.userId || "-",
        userName: r.userName || "Unknown",
        userEmail: r.userEmail || "-",
        level: formatLevel(r.level, r.division),
        rewardAmount: `$${(Number(r.finalRewardAmount) || 0).toFixed(2)}`,
        cryptoAmount: `$${(Number(r.cryptoAmount) || 0).toFixed(2)}`,
        retopupAmount: `$${(Number(r.retopupAmount) || 0).toFixed(2)}`,
        walletAddress: r.walletAddress || "-",
        accountNumber: r.accountNumber || "-",
        ifscCode: r.ifscCode || "-",
        date: r.createdAt ? formatDate(r.createdAt) : "-",
      }));

      setHistoryList(formattedHistory);
      setPendingList(formattedPending);
      setTotalWithdrawals(historyData.length);
      setTodayWithdrawals(todayCount);
      setTotalAmount(totalAmountValue);
    } catch (error) {
      console.error("Error fetching hybrid withdrawals:", error);
      toast.error("Failed to load hybrid withdrawals");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const onSelectedRows = (selectedFlatRows) => {
    setSelectedRows(selectedFlatRows);
  };

  const handleApprove = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} level claim(s)?`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveLevelClaims(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving level claims");
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveManually = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} level claim(s) manually through bank account? No USDT will be sent on-chain.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveLevelClaimsManually(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved manually`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving level claims manually");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Reject ${selectedRows.length} level claim(s)? The level will be unlocked so the user can retry.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await rejectLevelClaims(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} rejected`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error rejecting level claims");
    } finally {
      setProcessing(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="investment-history">
      {processing && <Loader2 />}

      <h1 className="main-heading">All Hybrid Withdrawals</h1>
      <br />

      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Total Withdrawals</p>
            <h2 className="stat-value">{totalWithdrawals}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Today Withdrawals</p>
            <h2 className="stat-value">{todayWithdrawals}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Total Amount</p>
            <h2 className="stat-value">${totalAmount.toFixed(2)}</h2>
          </div>
        </div>
      </div>

      <br />

      <div className="hbw-tabs">
        <button
          className={`hbw-tab${activeTab === "request" ? " active" : ""}`}
          onClick={() => setActiveTab("request")}
          type="button"
        >
          Request ({pendingList.length})
        </button>
        <button
          className={`hbw-tab${activeTab === "history" ? " active" : ""}`}
          onClick={() => setActiveTab("history")}
          type="button"
        >
          History ({historyList.length})
        </button>
      </div>

      {activeTab === "request" ? (
        <>
          <div className="button-group" style={{ margin: "1rem 0" }}>
            <button
              onClick={handleReject}
              disabled={processing || !selectedRows.length}
              style={{ background: "red", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Reject Selected
            </button>
            <button
              onClick={handleApproveManually}
              disabled={processing || !selectedRows.length}
              style={{ background: "#2563eb", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Manually
            </button>
            <button
              onClick={handleApprove}
              disabled={processing || !selectedRows.length}
              style={{ background: "green", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Selected
            </button>
          </div>

          <CustomTableCheckbox
            columns={pendingColumns}
            data={pendingList}
            heading={`Pending Level Claims (${pendingList.length})`}
            onSelectedRows={onSelectedRows}
          />
        </>
      ) : (
        <CustomTable
          data={historyList}
          columns={historyColumns}
          heading={`Level Claims History (${historyList.length})`}
        />
      )}
    </div>
  );
}

export default AllHybridWithdrawals;
